<template>
  <q-input v-model="tableFilter" v-if="includeFilter" outlined dense placeholder="Search for a row..." class="q-mt-sm q-mb-sm print-hide"/>
    <div class="table-container" >
      <q-table
        :flat="flat"
        bordered
        :title="title"
        :rows="rows"
        :columns="columns"
        row-key="name"
        dense
        class="fixed-table q-pa-sm"
        :filter="tableFilter"
        :hide-pagination="hidePagination"
      >
      <template v-slot:header="props">
        <q-tr :props="props">
          <q-th
            v-for="col in props.cols"
            :key="col.name"
            :props="props"
            :style="col.style"
          >
            {{ col.label }}
            <q-tooltip v-if="mapLayer && mapLayer.includes('CAS')">{{ gearCodesPretty[col.label] }}</q-tooltip>
          </q-th>
        </q-tr>
      </template>

      <template v-slot:body="props">
        <q-tr :props="props">
          <q-td
            v-for="col in props.cols"
            :key="col.name"
            :props="props"
            :style="col.style"
          >
            <div class="row">
              <div class="col" @click="cellClick(col, props.row)" :class="getCursor(col, props.row)">
                <div v-if="labelCells || col.name === 'name'"
                  class="cell-label"
                  :class="col.name != 'name' ? 'absolute-center' : ''"
                  :style="getLabelColors(col.value)"
                >
                  {{ getLabelVal(col.value, col, props.row) }}
                </div>
                <q-icon class="cell-icon q-pa-none" name="circle"
                  :style="getColors(col.value, props.row, col)"
                  size="md"
                  :class="highlightClickedCell(props.row, col)"
                  v-if="col.name != 'name'"
                />
              </div>
  
            </div>
          </q-td>
        </q-tr>
        <q-tr v-show="props.expand" :props="props">
          <q-td colspan="100%">
            <div class="text-left">This is expand slot for row above: {{ props.row.name }}.</div>
          </q-td>
        </q-tr>
        
      </template>

      <template v-slot:pagination="props">
        <div class="row justify-between q-pa-sm">
          <q-btn
            flat
            round
            dense
            icon="chevron_left"
            @click="props.prevPage"
            :disable="props.isFirstPage"
            aria-label="Previous page"
          />
          <q-pagination
            v-model="props.pagination.page"
            :max="props.pagesNumber"
            aria-label="Table pagination"
          />
          <q-btn
            flat
            round
            dense
            icon="chevron_right"
            @click="props.nextPage"
            :disable="props.isLastPage"
            aria-label="Next page"
          />
        </div>
      </template>
    </q-table>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, toRefs, computed, PropType } from "vue";
import { QTableColumn } from 'quasar'
import { useMapItems } from "../composables/useMapItems";
import { useHelpers } from "../composables/useHelpers";

import { useRouter } from 'vue-router';
const router = useRouter();


const {  styleMap, setLegend, toggleLayer, map, tileLayers, mapStyleInfo} = useMapItems()
const { arrayFindObjectByProp, getKeyByValue, months, logGAevent } = useHelpers();

const passedProps = defineProps({
  version: {type: Number, default: null},
  rows:{type: Array, default: null},
  columns:{type: Array as PropType<QTableColumn[]>, default: null},
  flat:{type:Boolean, default: false}, // style table as falt
  hidePagination:{type:Boolean, default: false}, // hide pagination at bottom
  title: {type: String, default: null},
  expandRows: {type:Boolean, default: false},
  includeFilter:{type:Boolean, default: true},
  mapStyle: {type: Boolean, default: false},
  mapStops: {type: Array, default: null}, // array of map stops used if styling the map
  dqStops: {type: Array, default: null}, // array of dat quality map stops used if styling the map for the data quality version of the layer
  mapStopFunc: {type: Function, default: null}, //CAS thresholds are difference for each commnity, so need function to generate
  mapClassNames: {type: Object, default: null}, // object of names for map values used in legend.  e.g. { 90 : "Very High"}
  dqClassNames : {type: Object, default: null}, // object of names for map values used in legend.  e.g. { 1 : "Very High"}
  cellColors: {type:Object, default: null}, // an object version of the array passed in as mapStops
  colorFunc: {type: Function, default: null}, // a function used to define color ranges.  As in:    getColor(v) { if (v >= 75) return '#006d2c' 
                                                                                                      // else if (v >= 50 && v < 75) return '#31a354'
                                                                                                      // else if (v >= 25 && v < 50) return '#74c476'
                                                                                                      // else if (v >= 10 && v < 25) return '#bae4b3'
                                                                                                      // else if (v > 0 && v < 10) return '#edf8e9'
                                                                                                      // else return '#d1d1d1'
  labelColorFunc: {type: Function, default: null}, // same as above, but used for white labels if color is dark
  labelCells: {type:Boolean, default: true},
  mapStyleType: {type: String, default: 'categorical'}, // can also pass in 'interpolate' for continuous data
  mapLayer: {type: String, default: null}, // if a single layer is used, pass that in.  If this then should not be colTolayerObj
  mapLegendTitles : {type: Object, default: null}, // layer title for the legend.  e.g. Fish High Biomass (1990s)
  manualMapLegend: {type: Object, default: null}, // for CAS, easier to just pass in a plain object
  additionalClickFunc: {type: Function, default: null}, // osomething to happen on cell click.  For CAS, highlight the selected port point on map
  season: {type: String, default: null}, // fish by decade need the season so can get that layer on map click
  selSp: {type: Object, default: null}, // the selected species can be passed in, if selected elsewhere.  Needed to highlight the cell
  selMonth: {type: Object, default: null}, // the selected month (mammals, birds, turtles) can be passed in, if selected elsewhere.  Needed to highlight the cell
  selDecade: {type: Object, default: null}, // the selected decade (Fish) can be passed in, if selected elsewhere.  Needed to highlight the cell
  selCell: {type: String, default: null} // selected cell for custom table highlight (CAS)

})


const {  selSp, selMonth, selDecade, selCell, version, rows, columns, title, expandRows, includeFilter, mapStyle, dqStops, dqClassNames, mapStops, mapStopFunc, mapClassNames, cellColors, colorFunc, labelColorFunc, mapLayer, mapLegendTitles, manualMapLegend, season, additionalClickFunc, mapStyleType } = toRefs(passedProps)

const emit = defineEmits(['cellClick'])

const resultContentCol = ref(null)
const resultContentColWidth = ref(null)
if (resultContentCol.value){
    resultContentCol.value = resultContentCol.value.getBoundingClientRect().width;
}
  
  

  const colorOnGradient =(hex1, hex2, minVal, maxVal, value) => {
    // Convert hex to RGB
    function hexToRgb(hex) {
        let bigint = parseInt(hex.slice(1), 16);
        return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
    }

    // Convert RGB to hex
    function rgbToHex(r, g, b) {
        return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
    }

    // Normalize the value to a range between 0 and 1
    let t = (value - minVal) / (maxVal - minVal);
    t = Math.max(0, Math.min(1, t)); // Ensure t is within [0, 1]

    // Interpolate between the two colors
    let rgb1 = hexToRgb(hex1);
    let rgb2 = hexToRgb(hex2);
    let r = Math.round(rgb1[0] + t * (rgb2[0] - rgb1[0]));
    let g = Math.round(rgb1[1] + t * (rgb2[1] - rgb1[1]));
    let b = Math.round(rgb1[2] + t * (rgb2[2] - rgb1[2]));

    return rgbToHex(r, g, b);
  }

  const getColors = (v, row, colObj) =>{

    //this is for categorical attribtues where the colors are mapped to categorical values
    if (cellColors.value){
      const style = {'color' :  cellColors.value[v] }
      return style
    }
    
    // this is a function that is passed from the component
    // needed to apply more dynamic styles.. like for the CAS data
    // which uses different thresholds to define "High Intensity" for 
    // each community
    else if (colorFunc.value){
      let style
      if (mapLayer.value && mapLayer.value.includes("CAS")){ 
        const gearCode = gearCodesReverse[colObj.name];
        const portCode = getKeyByValue(portPrettyNames, row.name)
        const styleAttr = portCode + gearCode
        // pass the style attribtue into the color function (which coems from People)
        // Used so that if there is no community for that port+Gear combo, it can be styled
        // separately from a 0 value for a community that does exist 
        style = {'color' : colorFunc.value(v, styleAttr)}
      }
  
      return style
    }

    // if neither of above, use a gradient
    else{
      const style = {'color' : colorOnGradient('#d0daf5', '#012175', 0, 300, v)}
      return style
    }
  }

  const getLabelVal = (v, colObj, row) =>{
    if (mapLayer.value && mapLayer.value.includes("CAS")){ 
        // for the CAS data, don't show the label if there's no communtity
        const gearCode = gearCodesReverse[colObj.name];
        const portCode = getKeyByValue(portPrettyNames, row.name)
        const styleAttr = portCode + gearCode
        // if it's not the name attr & the returned color is white (no community) then don't show the 0 value
        if (!styleAttr.includes('undefined') && colorFunc.value(v, styleAttr) == 'white'){
          return ''
        }

        else{
          // otherwise show <1 for values <1 else the value
          if (v>0 && v < 1){return '<1'}
          else{
            // round the number so it shows up on the bubble, but next text label
            if (typeof v === 'number'){
              return Math.round(v)
            }
            else{
              return v
            }
          }
        }
    }
    else if(mapLayer.value && mapLayer.value.includes("CAS")){ 

    }
    else{
      // otherwise show <1 for values <1 else the value
      if (v>0 && v < 1){return '<1'}
      else{return v}
    }

  }

  const getLabelColors = (v) =>{
    // used so that dark icons can have a white label.  
    // Passed as a fcuntion from component.  See People component
    if (labelColorFunc.value){
      const style = {'color' : labelColorFunc.value(v)}
      return style
    }
    const style = {'color' :'black'}
    return style
  }


  const clickedCell = ref(null)

  watch(selCell, (newVal) => {
    if (newVal){
      clickedCell.value = newVal
    }
  }, { immediate: true })

  const cellClick = (colObj, row) =>{
    // console.log(colObj)
    // console.log(row)
    // if there's a function to run on cell click, pass that in
    if (additionalClickFunc.value){
      additionalClickFunc.value(colObj, row)
    }
    

    // if the cells can be clicked to style the map, this function
    // highlights the cell that was clicked
    if (mapStyle.value === true){
      // console.log(colObj);
      // console.log(row);
      let layerID;
      let sp
      layerID = mapStyleInfo.value['layer']
      
      //if the map is already StylePropertyMapReadOnly, use that
      if (mapStyleInfo.value['layer'] != undefined){
        layerID = mapStyleInfo.value['layer']
      }


      // if the map layer is passed in, then that's what it is
      else if(mapLayer.value){
        layerID = mapLayer.value;
      }

      // for fish the season is in the tile layer name
      else if(season.value){
        sp = row.spID;
        layerID = `FISH_TIME_${season.value}_${sp}` //colObj.name;

      }



      let styleAttr
      // getting attribute used to style the map is a bit different for each component
      if (layerID.includes("CAS")){  
        // the CAS attribute is based on the concatenation of teh PORT & GEAR
        const gearCode = gearCodesReverse[colObj.name];
        const portCode = getKeyByValue(portPrettyNames, row.name)

        styleAttr = portCode + gearCode
       
        // this is used to apply a highlight to the clicked cell in the table
        clickedCell.value = styleAttr;
      }
      if (layerID.includes("FISH")){  
        styleAttr = colObj.name;
        sp = row.spID;
        // this is used to apply a highlight to the clicked cell in the table
        clickedCell.value = sp + colObj.name;
        const decade = colObj.name;
        // console.log(colObj.name)
        const emitObj = {sp: row.spID, decade: decade}
        console.log(emitObj)
        emit('cellClick', emitObj)
      }
      if (layerID == "PEOPLE_OTHER"){  
        const month = colObj.field;
        const attr = row.id;
        styleAttr = attr + "_" + month.toUpperCase()
        // console.log(styleAttr)
        // this is used to apply a highlight to the clicked cell in the table
        clickedCell.value = attr + "_" + month
        
      }

      else if (layerID.includes("MAMMAL") || layerID.includes("BIRD") || layerID.includes("TURTLE") ){
        const monthCode = getKeyByValue(months, colObj.name)
        styleAttr = row.spID + "_" + monthCode
        // console.log(sp)
        // console.log(row.spID)
        clickedCell.value = styleAttr;
        const emitObj = {sp: row.spID, month: monthCode}
        emit('cellClick', emitObj)
      }


      // console.log(clickedCell.value)
      // console.log(`style ${layerID} by ${sp}`)
      const layer = arrayFindObjectByProp(tileLayers.value, 'id', layerID)
     
      logGAevent("table_click", layerID, styleAttr)
  
      // if the layer isn't in the map, toggle it on
      // this happens when moving between decades
      let theseMapStops
      
      // if a data quality layer is being shown, get the map stops fo data qaulity 
      if (layerID.includes("QUALITY")){
        theseMapStops = dqStops.value
      }

      else if (mapStops.value){theseMapStops = mapStops.value}
      else if (mapStopFunc){ // CAS data
        theseMapStops = mapStopFunc.value(clickedCell.value)
      }

      // THIS WAS CONFLICTING WITH STYLING DONE FROM COMPONENTS FOR SPECIES TABLES. THIS CODE PREDATED THE SWITCH TO DROPDOWNS
      // SO ONLY RUN FOR PEOPLE LAYERS
      if (layerID.includes("CAS") || layerID== "PEOPLE_OTHER"){ 
        if (!map.value.getLayer(layerID)) {
          toggleLayer(true, layer, null) // null value -- no need to zoom to the polygon, since only styling, not querying
          map.value.once('idle', function(){
            styleMap(layerID, styleAttr, mapStyleType.value,  theseMapStops, mapClassNames.value)
          })
        }
        // changing attr within a layer already in the map
        else{
          styleMap(layerID, styleAttr, mapStyleType.value,  theseMapStops, mapClassNames.value)
        }
      }

      // console.log(theseMapStops)
      if (manualMapLegend.value){
        const name = colObj.label + ": " + row.name 
        setLegend(name, manualMapLegend.value )
      }
      else if (layerID.includes("QUALITY")){
        setLegend(row.name + " " + mapLegendTitles.value[colObj.name] + " Data Confidence",  theseMapStops, dqClassNames.value)
      }
      else if (mapLegendTitles.value){
        setLegend(row.name + " " + mapLegendTitles.value[colObj.name],  theseMapStops, mapClassNames.value)
      }
      else{
        setLegend(colObj.name,  theseMapStops, mapClassNames.value)
      }
      
    }
    
  }

const highlightClickedCell = (row, col)=>{

  // fish
  if ((row.spID && (row.spID + col.name == clickedCell.value)) || 
    (selSp.value && selDecade.value && selSp.value.value + "_" + selDecade.value.value ==  row.spID + "_" +col.name)
  ){
    clickedCell.value = selSp.value.value + "_" + selDecade.value.value
    return 'highlight-table-cell'
  }

  //mammals & birds
  else if (

    (row.spID && (row.spID + "_" + getKeyByValue(months, col.name) == clickedCell.value)) ||
    ( selSp.value && selMonth.value && selSp.value.value + "_" + selMonth.value.value ==  row.spID + "_" + getKeyByValue(months, col.name))
  ){
    // if the highlighted cell is based on the values passed in from dropdowns, then set the clickCell value to that
    if (selSp.value && selMonth.value){ 
      clickedCell.value = selSp.value.value + "_" + selMonth.value.value
      return 'highlight-table-cell'
    }
    
  }

  // AIS data
  else if (row.id && (row.id + "_" + col.name == clickedCell.value)){
     return 'highlight-table-cell'
  }

  // CAS
  else if (gearCodesReverse[col.name]){
        const gearCode = gearCodesReverse[col.name];
        const portCode = getKeyByValue(portPrettyNames, row.name)
        const styleAttr = portCode + gearCode
        if (styleAttr == clickedCell.value){
          return 'highlight-table-cell'
        }
  }


  // not highlighted
  else{
    return ''
  }

}
const tableFilter = ref('')
const gearCodesReverse ={
    "Groundfish65minus" : "GFMI",  
    "Groundfish65plus" : "GFPL",  
    "Longline" : "LONG",  
    "Dredge" : "DRED",  
    "Lobster" : "LOBS",  
    "Gillnet" : "GILL",  
    "ShrimpTrawl" : "SHRM",  
    "OtherPotsTraps" : "OPTR",
    "MidwaterSeine" : "MSEI"  
};
const portPrettyNames = {
    "CTST" : "Stonington,  CT",
    "MABS" : "Boston,  MA",
    "MABV" : "Beverly,  MA",
    "MACH" : "Chatham,  MA",
    "MADN" : "Dennis,  MA",
    "MAFH" : "Fairhaven,  MA",
    "MAGL" : "Gloucester,  MA",
    "MAHL" : "Hull,  MA",
    "MAHP" : "Harwichport,  MA",
    "MAMF" : "Marshfield,  MA",
    "MAMH" : "Marblehead,  MA",
    "MANB" : "New Bedford,  MA",
    "MANP" : "Newburyport,  MA",
    "MAPL" : "Plymouth,  MA",
    "MAPT" : "Provincetown,  MA",
    "MARP" : "Rockport,  MA",
    "MASC" : "Scituate,  MA",
    "MASW" : "Sandwich,  MA",
    "MAWP" : "Westport,  MA",
    "MDOC" : "Ocean City,  MD",
    "MEBA" : "Bailey Island,  ME",
    "MEBE" : "Beals Island,  ME",
    "MEBO" : "Boothbay Harbor,  ME",
    "MECH" : "Cundys Harbor,  ME",
    "MECP" : "Cape Porpoise,  ME",
    "MEHW" : "Harpswell,  ME",
    "MEJP" : "Jonesport,  ME",
    "MEKB" : "Kennebunkport,  ME",
    "MEKT" : "Kittery,  ME",
    "MEPO" : "Portland,  ME",
    "MERK" : "Rockland,  ME",
    "MESB" : "Stueben,  ME",
    "MESG" : "Stonington,  ME",
    "MESH" : "Sprucehead,  ME",
    "METH" : "Tenants Harbor,  ME",
    "MEVH" : "Vinalhaven,  ME",
    "MEWH" : "Winter Harbor,  ME",
    "MEYH" : "York Harbor,  ME",
    "NCBE" : "Beaufort,  NC",
    "NCEN" : "Engelhard,  NC",
    "NCHA" : "Hatteras,  NC",
    "NCOR" : "Oriental,  NC",
    "NCWC" : "Wanchese,  NC",
    "NHNW" : "Newington,  NH",
    "NHPM" : "Portsmouth,  NH",
    "NHRY" : "Rye,  NH",
    "NHSB" : "Seabrook,  NH",
    "NJAT" : "Atlantic City,  NJ",
    "NJBF" : "Belford,  NJ",
    "NJBG" : "Barnegat,  NJ",
    "NJCM" : "Cape May,  NJ",
    "NJPP" : "Point Pleasant,  NJ",
    "NJSC" : "Sea Isle City,  NJ",
    "NJSR" : "Shark RIver,  NJ",
    "NJWW" : "Wildwood,  NJ",
    "NYFR" : "Freeport,  NY",
    "NYMO" : "Montauk,  NY",
    "NYPL" : "Point Lookout,  NY",
    "NYSC" : "Shinnecock,  NY",
    "RILC" : "Little Compton,  RI",
    "RINP" : "Newport,  RI",
    "RIPJ" : "Point Judith,  RI",
    "RITI" : "Tiverton,  RI",
    "VACH" : "Chincoteague,  VA",
    "VACS" : "City Of Seaford,  VA",
    "VAHA" : "Hampton,  VA",
    "VANN" : "Newport News,  VA",
    "VAVB" : "Virginia Beach,  VA"
}

const gearCodesPretty ={
    "GFMI" : "Groundfish <65'",
    "GFPL" : "Groundfish >65'",
    "LONG" : "Longline",
    "DRED" : "Dredge",
    "LOBS" : "Lobster Pots",
    "GILL" : "Gillnet",
    "SHRM" : "Shrimp Trawl",
    "OPTR" : "Other Pots & Traps",
    "MSEI" : "Midwater Seine"
};

const getCursor = (colObj, row) =>{
  if (mapStyle.value){
      if (mapLayer.value && mapLayer.value.includes("CAS")){  
        // some communities don't exist (e.g. Cundy's Harbor Gillnet).
        // So don't show the cursor as a pointer
        const gearCode = gearCodesReverse[colObj.name];
        const portCode = getKeyByValue(portPrettyNames, row.name)
        const styleAttr = portCode + gearCode
        const stops = mapStopFunc.value(styleAttr)
        if (!stops.includes(999)){
          return 'pointer'
        }
        else{
          return 'disable-click'
        }
      }
      else{
        return 'pointer'
      }
  }
  else{ return ''}

}

const decades = [
    {"value" : "d2010", "label" : "Current"},
    {"value" : "d2000", "label" : "2000s"},
    {"value" : "d1990", "label" : "1990s"},
    {"value" : "d1980", "label" : "1980s"},
    {"value" : "d1970", "label" : "1970s"}
]

  // DATA FORMAT
  // TS error on coluns: See https://github.com/quasarframework/quasar/issues/13036#issuecomment-1086695817
  // const columns: QTableColumn[] = [
  //   {
  //     name: 'name',
  //     required: true,
  //     label: 'Demersal Fish',
  //     align: 'left',
  //     field: row => row.name,
  //     format: val => `${val}`,
  //     sortable: true
  //   },
  //   { name: 'calories', align: 'center', label: 'Jan', field: 'calories', sortable: true },
  //   { name: 'fat', align: 'center', label: 'Feb', field: 'fat', sortable: true },
  //   { name: 'carbs', align: 'center', label: 'Mar', field: 'carbs' },
  //   { name: 'protein', align: 'center', label: 'Apr', field: 'protein' },
  //   { name: 'sodium',align: 'center', label: 'May', field: 'sodium' },
  //   { name: 'calcium', align: 'center', label: 'Jun', field: 'calcium', sortable: true, sort: (a, b) => parseInt(a, 10) - parseInt(b, 10) },
  //   { name: 'iron', align: 'center', label: 'Jul', field: 'iron', sortable: true, sort: (a, b) => parseInt(a, 10) - parseInt(b, 10) }
  // ]

  // const rows = [
  //   {
  //     name: 'American Plaice',
  //     calories: 159,
  //     fat: 6.0,
  //     carbs: 24,
  //     protein: 4.0,
  //     sodium: 87,
  //     calcium: '14%',
  //     iron: '1%'
  //   },
  //   {
  //     name: 'Goosefish',
  //     calories: 237,
  //     fat: 9.0,
  //     carbs: 37,
  //     protein: 4.3,
  //     sodium: 129,
  //     calcium: '8%',
  //     iron: '1%'
  //   },
  // ]
</script>
<style scoped>
.med-text{
  font-size: 14px;
}

.q-dark .text .d3-text{
  fill: white !important;
}
/* .my-table{
  overflow-x: scroll;
} */
.cell-label{
   z-index: 10;
   text-wrap: wrap;
}

.pointer{
  cursor: pointer;
}
.disable-click{
  pointer-events: none;
}
.highlight-table-cell{
  box-shadow: 5px 5px 5px #252525;
  border-radius: 50%;
}
.fixed-table{
  table-layout: fixed;
  width: 100%;
}

.table-container{
  overflow: auto;
  outline: none; /* Remove default focus outline */

}

.fixed-table:focus {
  outline: 2px solid #24302b; 
}

</style>
